
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { resolveCampaignStatusAndProgress } from '@/hooks/useCampaigns';

interface Campaign {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  platform_targeted: string[] | null;
  brand_id: string | null;
  brands?: {
    name: string;
  } | null;
}

interface Event {
  id: string;
  name: string;
  event_date: string;
  event_time: string | null;
  event_type: string;
  venue_address: string | null;
  campaign_id: string | null;
  campaigns?: {
    name: string;
  } | null;
}

export const useCalendarCampaigns = (startDate: Date, endDate: Date) => {
  return useQuery({
    queryKey: ['calendar-campaigns', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          id,
          name,
          start_date,
          end_date,
          status,
          platform_targeted,
          brand_id,
          brands (
            name
          )
        `)
        .or(`start_date.lte.${format(endDate, 'yyyy-MM-dd')},end_date.gte.${format(startDate, 'yyyy-MM-dd')}`)
        .is('deleted_at', null)
        .order('start_date', { ascending: true });

      if (error) throw error;

      return (data || []).map((campaign) => {
        const brandObj = Array.isArray(campaign.brands) ? campaign.brands[0] : campaign.brands;
        const { status: resolvedStatus } = resolveCampaignStatusAndProgress(
          campaign.start_date,
          campaign.end_date,
          campaign.status
        );
        return {
          ...campaign,
          status: resolvedStatus,
          brands: brandObj,
        } as Campaign;
      });
    },
  });
};

export const useCalendarEvents = (startDate: Date, endDate: Date) => {
  return useQuery({
    queryKey: ['calendar-events', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select(`
          id,
          name,
          event_date,
          event_time,
          event_type,
          venue_address,
          campaign_id,
          campaigns (
            name
          )
        `)
        .gte('event_date', format(startDate, 'yyyy-MM-dd'))
        .lte('event_date', format(endDate, 'yyyy-MM-dd'))
        .order('event_date', { ascending: true });

      if (error) throw error;
      return data as Event[];
    },
  });
};
